import { useEffect } from 'react'
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  Node,
  Edge
} from 'reactflow'
import 'reactflow/dist/style.css'
import type { Contact } from '@/types'

interface RelationshipMapProps {
  contatos: Contact[]
  companyId?: string
}

export default function RelationshipMap({ contatos }: RelationshipMapProps) {
  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const [edges, setEdges, onEdgesChange] = useEdgesState([])

  useEffect(() => {
    if (!contatos || contatos.length === 0) return

    // Position decisors at y: 50, and influencers at y: 250
    const newNodes: Node[] = contatos.map((c, i) => ({
      id: c.id,
      type: c.decisor ? 'input' : 'default',
      data: { label: `${c.nome}\n${c.cargo || ''}` },
      position: { x: i * 220 + 80, y: c.decisor ? 50 : 250 },
      style: {
        background: c.decisor
          ? 'rgba(99, 102, 241, 0.15)' // Brand tinted glass
          : 'rgba(255, 255, 255, 0.03)',
        border: c.decisor
          ? '2px solid var(--brand-primary)'
          : '1px solid var(--glass-border)',
        borderRadius: '8px',
        padding: '12px',
        color: 'var(--font-color-body)',
        fontFamily: 'Inter, sans-serif',
        fontSize: '12px',
        fontWeight: c.decisor ? '600' : '400',
        width: 180,
        textAlign: 'center',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      },
    }))

    const newEdges: Edge[] = []
    contatos.forEach((c) => {
      if (c.relacionamentosOrigem && c.relacionamentosOrigem.length > 0) {
        c.relacionamentosOrigem.forEach((rel) => {
          newEdges.push({
            id: `e-${rel.origemId}-${rel.destinoId}`,
            source: rel.origemId,
            target: rel.destinoId,
            label: rel.tipo,
            animated: true,
            style: { stroke: 'var(--brand-primary)', strokeWidth: 2 },
            labelStyle: {
              fill: 'var(--brand-primary)',
              fontWeight: 700,
              fontSize: 10,
              fontFamily: 'Inter, sans-serif',
            },
            labelBgStyle: {
              fill: 'var(--bg-card)',
              fillOpacity: 0.9,
              rx: 4,
            },
          })
        })
      }
    })

    setNodes(newNodes)
    setEdges(newEdges)
  }, [contatos, setNodes, setEdges])

  if (!contatos || contatos.length === 0) {
    return (
      <div className='flex items-center justify-center h-[350px] border border-[var(--glass-border)] rounded-lg bg-white/[0.02]'>
        <p className='text-sm text-[var(--font-color-muted)]'>Nenhum contato cadastrado para exibir no mapa.</p>
      </div>
    )
  }

  return (
    <div className='w-full h-[400px] bg-black/10 border border-[var(--glass-border)] rounded-lg overflow-hidden relative shadow-inner'>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        fitView
        proOptions={{ hideAttribution: true }}
      >
        <MiniMap
          nodeStrokeColor={(n) => (n.type === 'input' ? 'var(--brand-primary)' : 'rgba(255,255,255,0.1)')}
          nodeColor={(n) => (n.type === 'input' ? 'rgba(99, 102, 241, 0.3)' : 'rgba(255,255,255,0.05)')}
          maskColor='rgba(0, 0, 0, 0.4)'
          style={{ background: 'var(--bg-card)', border: '1px solid var(--glass-border)' }}
        />
        <Controls showInteractive={false} className='bg-[var(--bg-card)] border-[var(--glass-border)]' />
        <Background color='rgba(255,255,255,0.1)' gap={16} />
      </ReactFlow>
    </div>
  )
}
